import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../lib/auth';
import Icon from '../../components/Icon';
import { Banner, PageLoading, Select } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type {
  Area, VisitAnalytics, DashboardLayout, MyDashboard, VisitOutcome, TeamMember,
} from '../../types';

/**
 * Visit Analytics — the configurable management dashboard.
 *
 * One call to /visits/reports/analytics feeds every chart; the layout
 * (which KPIs and charts show, and their order) is a company default an
 * admin sets, with an optional per-user override. Managers see their own
 * team, admins the whole company — the scope is the server's.
 */

// ---- the widget catalogue -------------------------------------------
const CHART_KEYS = ['visits', 'outcome', 'leader', 'hour', 'area', 'flags', 'duration', 'face', 'scatter'] as const;
type ChartKey = typeof CHART_KEYS[number];
const CHART_TITLES: Record<ChartKey, string> = {
  visits: 'Visits over time', outcome: 'Outcome mix', leader: 'Rep leaderboard',
  hour: 'Visits by hour', area: 'Area coverage', flags: 'Flagged visits',
  duration: 'Time on site', face: 'Face check', scatter: 'Rep productivity',
};
// Grid span (of 12) per chart, so a wide chart pairs with a narrow one.
const SPAN: Record<ChartKey, number> = { visits: 8, outcome: 4, leader: 8, hour: 4, area: 6, flags: 6, duration: 6, face: 6, scatter: 12 };

const KPI_KEYS = ['visits', 'prod', 'time', 'cov', 'adh', 'orders', 'val', 'flag'] as const;
type KpiKey = typeof KPI_KEYS[number];
const KPI_TITLES: Record<KpiKey, string> = {
  visits: 'Visits', prod: 'Productive rate', time: 'Avg time on site', cov: 'Shop coverage',
  adh: 'Plan adherence', orders: 'Orders booked', val: 'Order value', flag: 'Flagged visits',
};
type SparkField = 'visits' | 'productivePct' | 'avgMinutes' | 'flaggedPct' | 'planAdherencePct' | 'orders' | 'orderValue';

const DEFAULT_LAYOUT: Required<DashboardLayout> = {
  charts: Object.fromEntries(CHART_KEYS.map((k) => [k, true])),
  order: [...CHART_KEYS],
  kpis: Object.fromEntries(KPI_KEYS.map((k) => [k, true])),
};

const OUTCOME_LABEL: Record<VisitOutcome, string> = {
  order_taken: 'Order taken', no_order: 'No order', shop_closed: 'Shop closed',
  owner_absent: 'Owner absent', job_done: 'Job done', job_pending: 'Job pending',
  follow_up: 'Follow-up', other: 'Other',
};

const GRID = '#eef1f5';
const AXIS = '#9ca3af';
const FAINT = '#dfe3ea';
const HUES = ['#2a78d6', '#1baf7a', '#eda100', '#e87ba4', '#7c6cf0', '#1ea0a0'];

// ---- date helpers ----------------------------------------------------
function iso(d: Date) { return d.toISOString().slice(0, 10); }                 // UTC — for date-only arithmetic
function isoLocal(d: Date) { const y = d.getFullYear(); const m = `${d.getMonth() + 1}`.padStart(2, '0'); const day = `${d.getDate()}`.padStart(2, '0'); return `${y}-${m}-${day}`; }
function today() { return isoLocal(new Date()); }
function addDays(s: string, n: number) { const d = new Date(s + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return iso(d); }
function firstOfMonth() { const d = new Date(); return isoLocal(new Date(d.getFullYear(), d.getMonth(), 1)); }
function startOfWeek() { const d = new Date(); const back = (d.getDay() + 6) % 7; d.setDate(d.getDate() - back); return isoLocal(d); }
function startOfYear() { const d = new Date(); return isoLocal(new Date(d.getFullYear(), 0, 1)); }
function inr(n: number) {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}k`;
  return `₹${Math.round(n)}`;
}
function num(n: number) { return n.toLocaleString('en-IN'); }

type Preset = 'week' | '7' | '30' | '90' | 'month' | 'year' | 'custom';

// ---- hover tooltip (shared) -----------------------------------------
interface Tip { x: number; y: number; content: ReactNode; }
function useTip() {
  const [tip, setTip] = useState<Tip | null>(null);
  const bind = (content: ReactNode) => ({
    onMouseMove: (e: React.MouseEvent) => setTip({ x: e.clientX, y: e.clientY, content }),
    onMouseLeave: () => setTip(null),
  });
  return { tip, bind };
}
function TipBox({ tip }: { tip: Tip | null }) {
  if (!tip) return null;
  return <div className="chart-tip" style={{ left: tip.x + 14, top: tip.y + 14 }}>{tip.content}</div>;
}

// =====================================================================
export default function VisitAnalyticsPage() {
  const toast = useToast();
  const { can } = useAuth();
  const isAdmin = can('company_admin', 'platform_admin');

  const [preset, setPreset] = useState<Preset>('30');
  const [from, setFrom] = useState(() => addDays(today(), -29));
  const [to, setTo] = useState(today);
  const [routeId, setRouteId] = useState('');
  const [userId, setUserId] = useState('');
  const [areas, setAreas] = useState<Area[]>([]);
  const [reps, setReps] = useState<{ id: string; name: string }[]>([]);

  const [data, setData] = useState<VisitAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  const [companyDefault, setCompanyDefault] = useState<DashboardLayout | null>(null);
  const [mine, setMine] = useState<DashboardLayout | null>(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [lbMetric, setLbMetric] = useState<'visits' | 'prod' | 'orders' | 'time'>('visits');
  const [selRep, setSelRep] = useState<string | null>(null);

  const layout = useMemo(() => normalize(mine ?? companyDefault ?? DEFAULT_LAYOUT), [mine, companyDefault]);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'week') { setFrom(startOfWeek()); setTo(today()); }
    else if (p === '7') { setFrom(addDays(today(), -6)); setTo(today()); }
    else if (p === '30') { setFrom(addDays(today(), -29)); setTo(today()); }
    else if (p === '90') { setFrom(addDays(today(), -89)); setTo(today()); }
    else if (p === 'month') { setFrom(firstOfMonth()); setTo(today()); }
    else if (p === 'year') { setFrom(startOfYear()); setTo(today()); }
    // 'custom' leaves the dates for the user to edit
  };

  const run = useCallback(async () => {
    setLoading(true); setSelRep(null);
    try {
      const res = await api.get<VisitAnalytics>('/visits/reports/analytics', { from, to, routeId: routeId || undefined, userId: userId || undefined });
      setData(res);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load analytics.');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, routeId, userId]);

  useEffect(() => { void run(); /* first load */ /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    api.get<Area[]>('/customers/routes').then(setAreas).catch(() => { /* filter optional */ });
    api.get<MyDashboard>('/settings/dashboard/me')
      .then((d) => { setCompanyDefault(d.companyDefault); setMine(d.mine); })
      .catch(() => { /* fall back to default layout */ });
    api.get<TeamMember[]>('/users', { role: 'sales_rep', status: 'active' })
      .then((us) => setReps(us.map((u) => ({ id: u.id, name: u.fullName }))))
      .catch(() => { /* rep filter optional */ });
  }, []);

  const k = data?.kpis;

  const kpiTiles = useMemo(() => {
    if (!k || !data) return [];
    const cur = k.current, prev = k.previous;
    const spark = (f: SparkField | null): number[] | null => f ? data.series.kpiSeries.map((b) => b[f]) : null;
    const all: { key: KpiKey; label: string; value: string; delta: Delta | null; spark: number[] | null; danger?: boolean }[] = [
      { key: 'visits', label: KPI_TITLES.visits, value: num(cur.visits), delta: relDelta(cur.visits, prev.visits, true), spark: spark('visits') },
      { key: 'prod', label: KPI_TITLES.prod, value: `${cur.productivePct}%`, delta: ptDelta(cur.productivePct, prev.productivePct, true), spark: spark('productivePct') },
      { key: 'time', label: KPI_TITLES.time, value: `${cur.avgMinutes}m`, delta: relDelta(cur.avgMinutes, prev.avgMinutes, true), spark: spark('avgMinutes') },
      { key: 'cov', label: KPI_TITLES.cov, value: `${cur.coveragePct}%`, delta: ptDelta(cur.coveragePct, prev.coveragePct, true), spark: null },
      ...(data.hasPlan ? [{ key: 'adh' as KpiKey, label: KPI_TITLES.adh, value: cur.planAdherencePct == null ? '—' : `${cur.planAdherencePct}%`, delta: ptDelta(cur.planAdherencePct ?? 0, prev.planAdherencePct ?? 0, true), spark: spark('planAdherencePct') }] : []),
      ...(data.ordersEnabled ? [
        { key: 'orders' as KpiKey, label: KPI_TITLES.orders, value: num(cur.orders), delta: relDelta(cur.orders, prev.orders, true), spark: spark('orders') },
        { key: 'val' as KpiKey, label: KPI_TITLES.val, value: inr(cur.orderValue), delta: relDelta(cur.orderValue, prev.orderValue, true), spark: spark('orderValue') },
      ] : []),
      { key: 'flag', label: KPI_TITLES.flag, value: `${cur.flaggedPct}%`, delta: ptDelta(cur.flaggedPct, prev.flaggedPct, false), spark: spark('flaggedPct'), danger: true },
    ];
    return all.filter((t) => layout.kpis[t.key] !== false);
  }, [k, data, layout]);

  return (
    <>
      <PageHeader
        title="Visit analytics"
        subtitle="KPIs and charts for the range and beat you pick. Managers see their team; admins see everyone."
        actions={(
          <div className="row gap-8">
            <button type="button" className="btn btn-secondary" onClick={() => setCfgOpen(true)}>
              <Icon name="sliders" size={15} /> Customize
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void run()}>
              <Icon name="history" size={15} /> Refresh
            </button>
          </div>
        )}
      />

      <div className="page col gap-16">
        <div className="card" style={{ padding: 14 }}>
          <div className="row gap-12" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 160 }}>
              <Select label="Period" value={preset} onChange={(e) => applyPreset(e.target.value as Preset)}>
                <option value="week">This week</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="month">This month</option>
                <option value="year">This year</option>
                <option value="custom">Custom range</option>
              </Select>
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>From</label>
              <input type="date" className="input" value={from} onChange={(e) => { setFrom(e.target.value); setPreset('custom'); }} />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>To</label>
              <input type="date" className="input" value={to} onChange={(e) => { setTo(e.target.value); setPreset('custom'); }} />
            </div>
            <div style={{ minWidth: 180 }}>
              <Select label="Beat / area" value={routeId} onChange={(e) => setRouteId(e.target.value)}>
                <option value="">All areas</option>
                {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </div>
            <div style={{ minWidth: 170 }}>
              <Select label="Rep" value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">All reps</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Select>
            </div>
            <button type="button" className="btn btn-primary" onClick={() => void run()}>Apply</button>
          </div>
        </div>

        {loading ? <PageLoading rows={6} /> : !data || !k ? (
          <Banner kind="info">No data for this range yet.</Banner>
        ) : (
          <>
            {kpiTiles.length > 0 && (
              <div className="kpi-grid">
                {kpiTiles.map((t) => (
                  <KpiTile key={t.key} label={t.label} value={t.value} delta={t.delta}
                    spark={t.spark} sparkColor={t.danger ? 'var(--danger)' : 'var(--brand)'} />
                ))}
              </div>
            )}

            <div className="analytics-grid">
              {layout.order
                .filter((key): key is ChartKey => (CHART_KEYS as readonly string[]).includes(key) && layout.charts[key] !== false)
                .map((key) => (
                  <ChartCard key={key} span={SPAN[key]} title={CHART_TITLES[key]} sub={subFor(key, data)}
                    right={key === 'leader' ? (
                      <div className="tabs tabs-sm">
                        {(['visits', 'prod', 'orders', 'time'] as const).map((m) => (
                          <button key={m} type="button" className={`tab${lbMetric === m ? ' active' : ''}`} onClick={() => setLbMetric(m)}>
                            {m === 'visits' ? 'Visits' : m === 'prod' ? 'Prod %' : m === 'orders' ? 'Orders' : 'Avg time'}
                          </button>
                        ))}
                      </div>
                    ) : undefined}>
                    {renderChart(key, data, { lbMetric, selRep, setSelRep })}
                  </ChartCard>
                ))}
            </div>

            {layout.order.every((key) => layout.charts[key as ChartKey] === false) && (
              <Banner kind="info">No charts selected — open Customize to add some.</Banner>
            )}
          </>
        )}
      </div>

      {cfgOpen && (
        <CustomizeDrawer
          isAdmin={isAdmin} companyDefault={companyDefault} mine={mine}
          onClose={() => setCfgOpen(false)}
          onSaveCompany={async (l) => { await api.patch('/settings', { dashboardLayout: l }); setCompanyDefault(l); toast.success('Company default saved.'); }}
          onSaveMine={async (l) => { const res = await api.put<MyDashboard>('/settings/dashboard/me', { layout: l }); setMine(res.mine); toast.success(l ? 'Your view saved.' : 'Following the company default.'); }}
        />
      )}
    </>
  );
}

// ---- layout helpers --------------------------------------------------
function normalize(l: DashboardLayout | null | undefined): Required<DashboardLayout> {
  const charts = { ...DEFAULT_LAYOUT.charts, ...(l?.charts ?? {}) };
  const kpis = { ...DEFAULT_LAYOUT.kpis, ...(l?.kpis ?? {}) };
  const order = (l?.order ?? []).filter((k) => (CHART_KEYS as readonly string[]).includes(k));
  for (const k of CHART_KEYS) if (!order.includes(k)) order.push(k);
  return { charts, kpis, order };
}
function subFor(key: ChartKey, d: VisitAnalytics): string {
  switch (key) {
    case 'visits': return `${d.bucket === 'week' ? 'Weekly' : 'Daily'} — productive vs. other`;
    case 'outcome': return 'What the calls came to';
    case 'leader': return 'Top reps for the range';
    case 'hour': return 'When reps are on the beat';
    case 'area': return 'Shops visited vs. total in each beat';
    case 'flags': return 'Integrity checks over the period';
    case 'duration': return 'Visit length — short ones in red';
    case 'face': return 'Check-in selfie verification';
    case 'scatter': return 'x = visits · y = productive % · size = orders';
  }
}

interface Delta { label: string; good: boolean; flat: boolean; }
function relDelta(cur: number, prev: number, higherIsBetter: boolean): Delta {
  if (!prev) return { label: cur ? 'new' : '—', good: (cur > 0) === higherIsBetter, flat: !cur };
  const rel = ((cur - prev) / Math.abs(prev)) * 100;
  const flat = Math.abs(rel) < 0.5;
  return { label: `${rel > 0 ? '▲' : rel < 0 ? '▼' : '–'} ${Math.abs(rel).toFixed(Math.abs(rel) < 10 ? 1 : 0)}%`, good: (rel >= 0) === higherIsBetter, flat };
}
function ptDelta(cur: number, prev: number, higherIsBetter: boolean): Delta {
  const d = cur - prev; const flat = Math.abs(d) < 0.5;
  return { label: `${d > 0 ? '▲' : d < 0 ? '▼' : '–'} ${Math.abs(d).toFixed(1)} pts`, good: (d >= 0) === higherIsBetter, flat };
}

// ---- KPI tile --------------------------------------------------------
function KpiTile({ label, value, delta, spark, sparkColor }: {
  label: string; value: string; delta: Delta | null; spark: number[] | null; sparkColor: string;
}) {
  return (
    <div className="kpi-tile">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-foot">
        {delta ? (
          <span className={`delta-pill ${delta.flat ? 'flat' : delta.good ? 'up' : 'down'}`}>{delta.label}</span>
        ) : <span />}
        {spark && spark.length > 1 ? <Sparkline values={spark} color={sparkColor} /> : <span />}
      </div>
    </div>
  );
}
function Sparkline({ values, color }: { values: number[]; color: string }) {
  const w = 72, h = 26, pad = 3;
  const mn = Math.min(...values), mx = Math.max(...values);
  const sx = (i: number) => pad + i * (w - 2 * pad) / Math.max(1, values.length - 1);
  const sy = (v: number) => h - pad - (mx === mn ? 0.5 : (v - mn) / (mx - mn)) * (h - 2 * pad);
  const pts = values.map((v, i) => `${sx(i).toFixed(1)},${sy(v).toFixed(1)}`).join(' ');
  const last = values.length - 1;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }} aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={sx(last)} cy={sy(values[last]!)} r={2.4} fill={color} />
    </svg>
  );
}

// ---- chart card ------------------------------------------------------
function ChartCard({ span, title, sub, right, children }: {
  span: number; title: string; sub?: string; right?: ReactNode; children: ReactNode;
}) {
  return (
    <div className="card card-pad chart-card" style={{ gridColumn: `span ${span}` }}>
      <div className="card-head" style={{ marginBottom: 12, alignItems: 'flex-start' }}>
        <div><h3>{title}</h3>{sub ? <p className="small faint" style={{ margin: 0 }}>{sub}</p> : null}</div>
        {right}
      </div>
      {children}
    </div>
  );
}
function ChartLegend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="row gap-12" style={{ flexWrap: 'wrap', marginTop: 10, fontSize: 12.5 }}>
      {items.map((it) => (
        <span key={it.label} className="row gap-8" style={{ alignItems: 'center' }}>
          <span style={{ width: 11, height: 11, borderRadius: 3, background: it.color, display: 'inline-block' }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// =====================================================================
// chart dispatch
// =====================================================================
function renderChart(
  key: ChartKey, d: VisitAnalytics,
  ctx: { lbMetric: 'visits' | 'prod' | 'orders' | 'time'; selRep: string | null; setSelRep: (id: string | null) => void },
) {
  switch (key) {
    case 'visits': return <StackedBars
      series={d.series.visitsOverTime.map((p) => ({ label: p.label, parts: [p.productive, p.other] }))}
      names={['Productive', 'Other']} colors={['var(--brand)', FAINT]}
      legend={[{ label: 'Productive', color: 'var(--brand)' }, { label: 'Other', color: FAINT }]} />;
    case 'outcome': return <Donut items={d.series.outcomeMix.map((o, i) => ({ label: OUTCOME_LABEL[o.key as VisitOutcome] ?? o.key, value: o.count, color: HUES[i % HUES.length] ?? '#9ca3af' }))} />;
    case 'leader': return <Leaderboard reps={d.reps} metric={ctx.lbMetric} selRep={ctx.selRep} onSelect={ctx.setSelRep} />;
    case 'hour': return <VBars bars={fillHours(d.series.byHour)} color="var(--brand)" unit="visits" />;
    case 'area': return <><GroupedBars rows={d.series.areaCoverage.map((a) => ({ label: a.route, a: a.inBeat, b: a.visited }))}
      names={['In beat', 'Visited']} colorA={FAINT} colorB="var(--brand)" />
      <ChartLegend items={[{ label: 'In beat', color: FAINT }, { label: 'Visited', color: 'var(--brand)' }]} /></>;
    case 'flags': return <><StackedBars
      series={d.series.flagsOverTime.map((p) => ({ label: p.label, parts: [p.far, p.face, p.offPlan, p.noSign] }))}
      names={['Far from shop', 'Face low-confidence', 'Off-plan', 'No signature']}
      colors={['#e5484d', '#eda100', '#e87ba4', '#2a78d6']} />
      <ChartLegend items={[{ label: 'Far from shop', color: '#e5484d' }, { label: 'Face low-confidence', color: '#eda100' }, { label: 'Off-plan', color: '#e87ba4' }, { label: 'No signature', color: '#2a78d6' }]} /></>;
    case 'duration': return <VBars bars={d.series.durationBuckets.map((b, i) => ({ x: b.label, v: b.count, color: i === 0 ? 'var(--danger)' : 'var(--brand)' }))} unit="visits" />;
    case 'face': return <Donut items={[
      { label: 'Passed', value: d.series.faceCheck.verified, color: 'var(--success)' },
      { label: 'Low-confidence', value: d.series.faceCheck.review, color: 'var(--warning)' },
      { label: 'Rejected', value: d.series.faceCheck.rejected, color: 'var(--danger)' },
      { label: 'Not enrolled / skipped', value: d.series.faceCheck.skipped, color: '#9ca3af' },
    ]} />;
    case 'scatter': return <Scatter reps={d.reps} selRep={ctx.selRep} />;
  }
}
function fillHours(rows: { hour: number; count: number }[]) {
  const by = new Map(rows.map((r) => [r.hour, r.count]));
  const lo = Math.min(9, ...(rows.length ? rows.map((r) => r.hour) : [9]));
  const hi = Math.max(18, ...(rows.length ? rows.map((r) => r.hour) : [18]));
  const out: { x: string; v: number }[] = [];
  for (let h = lo; h <= hi; h++) out.push({ x: `${(h % 12) || 12}${h < 12 ? 'a' : 'p'}`, v: by.get(h) ?? 0 });
  return out;
}

// =====================================================================
// inline-SVG charts (no chart library) with hover tooltips
// =====================================================================
function StackedBars({ series, names, colors, legend }: {
  series: { label: string; parts: number[] }[]; names: string[]; colors: string[]; legend?: { label: string; color: string }[];
}) {
  const { tip, bind } = useTip();
  const W = 760, H = 200, padL = 30, padB = 24, padT = 8;
  const innerW = W - padL - 8, innerH = H - padT - padB;
  const totals = series.map((s) => s.parts.reduce((a, b) => a + b, 0));
  const max = Math.max(1, ...totals);
  if (series.length === 0) return <p className="faint small">Nothing in this range.</p>;
  const slot = innerW / series.length;
  const barW = Math.max(3, Math.min(30, slot * 0.6));
  const every = Math.ceil(series.length / 10);
  return (
    <div className="table-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, fontVariantNumeric: 'tabular-nums' }} role="img" aria-label="Stacked bars">
        {[0, 0.5, 1].map((t) => { const y = padT + innerH - t * innerH; return (
          <g key={t}><line x1={padL} y1={y} x2={W - 8} y2={y} stroke={GRID} /><text x={0} y={y + 3.5} fontSize="10" fill={AXIS}>{Math.round(max * t)}</text></g>
        ); })}
        {series.map((s, i) => {
          const cx = padL + i * slot + slot / 2;
          const total = totals[i] ?? 0;
          let yAcc = padT + innerH;
          const tipContent = (
            <div><strong>{s.label}</strong>{s.parts.map((v, pi) => (
              <div key={pi} className="tip-row"><span className="tip-dot" style={{ background: colors[pi % colors.length] }} />{names[pi]}: {v}</div>
            ))}<div className="tip-total">Total {total}</div></div>
          );
          const last = s.parts.length - 1;
          return (
            <g key={i} {...bind(tipContent)}>
              {s.parts.map((v, pi) => {
                if (v <= 0) return null;
                const h = (v / max) * innerH; yAcc -= h;
                const rx = pi === last ? 3 : 0;
                return <rect key={pi} x={cx - barW / 2} y={yAcc} width={barW} height={h} rx={rx} fill={colors[pi % colors.length]} stroke="#fff" strokeWidth={0.75} />;
              })}
              {i % every === 0 ? <text x={cx} y={H - 7} fontSize="10" fill={AXIS} textAnchor="middle">{s.label}</text> : null}
            </g>
          );
        })}
      </svg>
      {legend ? <ChartLegend items={legend} /> : null}
      <TipBox tip={tip} />
    </div>
  );
}

function GroupedBars({ rows, names, colorA, colorB }: {
  rows: { label: string; a: number; b: number }[]; names: string[]; colorA: string; colorB: string;
}) {
  const { tip, bind } = useTip();
  const W = 760, H = 200, padL = 30, padB = 26, padT = 8;
  const innerW = W - padL - 8, innerH = H - padT - padB;
  const max = Math.max(1, ...rows.flatMap((r) => [r.a, r.b]));
  if (rows.length === 0) return <p className="faint small">No beats in scope.</p>;
  const slot = innerW / rows.length;
  const barW = Math.max(3, Math.min(20, slot * 0.3));
  const yFor = (v: number) => padT + innerH - (v / max) * innerH;
  return (
    <div className="table-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, fontVariantNumeric: 'tabular-nums' }} role="img" aria-label="Grouped bars">
        {[0, 0.5, 1].map((t) => { const y = padT + innerH - t * innerH; return (
          <g key={t}><line x1={padL} y1={y} x2={W - 8} y2={y} stroke={GRID} /><text x={0} y={y + 3.5} fontSize="10" fill={AXIS}>{Math.round(max * t)}</text></g>
        ); })}
        {rows.map((r, i) => {
          const cx = padL + i * slot + slot / 2;
          const tipContent = <div><strong>{r.label}</strong><div className="tip-row"><span className="tip-dot" style={{ background: colorA }} />{names[0]}: {r.a}</div><div className="tip-row"><span className="tip-dot" style={{ background: colorB }} />{names[1]}: {r.b}</div></div>;
          return (
            <g key={i} {...bind(tipContent)}>
              <rect x={cx - barW - 1} y={yFor(r.a)} width={barW} height={padT + innerH - yFor(r.a)} rx={2} fill={colorA} />
              <rect x={cx + 1} y={yFor(r.b)} width={barW} height={padT + innerH - yFor(r.b)} rx={2} fill={colorB} />
              <text x={cx} y={H - 8} fontSize="10" fill={AXIS} textAnchor="middle">{r.label.length > 10 ? r.label.slice(0, 9) + '…' : r.label}</text>
            </g>
          );
        })}
      </svg>
      <TipBox tip={tip} />
    </div>
  );
}

function VBars({ bars, color, unit }: {
  bars: { x: string; v: number; color?: string }[]; color?: string; unit: string;
}) {
  const { tip, bind } = useTip();
  const W = 760, H = 190, padL = 30, padB = 22, padT = 8;
  const innerW = W - padL - 8, innerH = H - padT - padB;
  const max = Math.max(1, ...bars.map((b) => b.v));
  if (bars.length === 0) return <p className="faint small">Nothing in this range.</p>;
  const slot = innerW / bars.length;
  const barW = Math.max(4, Math.min(44, slot * 0.66));
  const yFor = (v: number) => padT + innerH - (v / max) * innerH;
  return (
    <div className="table-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, fontVariantNumeric: 'tabular-nums' }} role="img" aria-label="Bars">
        {[0, 0.5, 1].map((t) => { const y = padT + innerH - t * innerH; return (
          <g key={t}><line x1={padL} y1={y} x2={W - 8} y2={y} stroke={GRID} /><text x={0} y={y + 3.5} fontSize="10" fill={AXIS}>{Math.round(max * t)}</text></g>
        ); })}
        {bars.map((b, i) => {
          const cx = padL + i * slot + slot / 2;
          return (
            <g key={i} {...bind(<div><strong>{b.x}</strong><div>{num(b.v)} {unit}</div></div>)}>
              <rect x={cx - barW / 2} y={yFor(b.v)} width={barW} height={padT + innerH - yFor(b.v)} rx={3} fill={b.color || color || 'var(--brand)'} />
              <text x={cx} y={H - 6} fontSize="10" fill={AXIS} textAnchor="middle">{b.x}</text>
            </g>
          );
        })}
      </svg>
      <TipBox tip={tip} />
    </div>
  );
}

function Donut({ items }: { items: { label: string; value: number; color: string }[] }) {
  const { tip, bind } = useTip();
  const total = items.reduce((a, b) => a + b.value, 0);
  if (total === 0) return <p className="faint small">Nothing in this range.</p>;
  const R = 70, r = 44, C = 90;
  let acc = 0;
  const arcs = items.filter((it) => it.value > 0).map((it) => {
    const frac = it.value / total; const a0 = acc * 2 * Math.PI - Math.PI / 2; acc += frac; const a1 = acc * 2 * Math.PI - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const p = (ang: number, rad: number) => `${C + rad * Math.cos(ang)} ${C + rad * Math.sin(ang)}`;
    return { it, d: `M ${p(a0, R)} A ${R} ${R} 0 ${large} 1 ${p(a1, R)} L ${p(a1, r)} A ${r} ${r} 0 ${large} 0 ${p(a0, r)} Z` };
  });
  return (
    <div className="row gap-16" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
      <svg viewBox="0 0 180 180" width="170" height="170" role="img" aria-label="Donut chart">
        {arcs.map((a, i) => (
          <path key={i} d={a.d} fill={a.it.color} stroke="#fff" strokeWidth={1.5}
            {...bind(<div><strong>{a.it.label}</strong><div>{num(a.it.value)} · {Math.round((a.it.value / total) * 100)}%</div></div>)} />
        ))}
        <text x="90" y="86" textAnchor="middle" fontSize="22" fontWeight="700" fill="var(--ink)">{num(total)}</text>
        <text x="90" y="104" textAnchor="middle" fontSize="11" fill={AXIS}>total</text>
      </svg>
      <div className="col gap-8" style={{ minWidth: 160, flex: 1 }}>
        {items.filter((it) => it.value > 0).map((it) => (
          <div key={it.label} className="row gap-8" style={{ alignItems: 'center', fontSize: 13 }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: it.color }} />
            <span style={{ flex: 1 }}>{it.label}</span>
            <span className="num" style={{ fontWeight: 600 }}>{num(it.value)}</span>
            <span className="faint" style={{ width: 42, textAlign: 'right' }}>{Math.round((it.value / total) * 100)}%</span>
          </div>
        ))}
      </div>
      <TipBox tip={tip} />
    </div>
  );
}

function Leaderboard({ reps, metric, selRep, onSelect }: {
  reps: VisitAnalytics['reps']; metric: 'visits' | 'prod' | 'orders' | 'time'; selRep: string | null; onSelect: (id: string | null) => void;
}) {
  const valOf = (r: VisitAnalytics['reps'][number]) =>
    metric === 'visits' ? r.visits : metric === 'prod' ? r.productivePct : metric === 'orders' ? r.orders : r.avgMinutes;
  const unit = metric === 'prod' ? '%' : metric === 'time' ? 'm' : '';
  const rows = [...reps].sort((a, b) => valOf(b) - valOf(a)).slice(0, 10);
  const max = Math.max(1, ...rows.map(valOf));
  if (rows.length === 0) return <p className="faint small">No reps in scope.</p>;
  return (
    <div className="col gap-8">
      {rows.map((r) => {
        const v = valOf(r); const on = selRep === r.userId;
        return (
          <button key={r.userId} type="button" onClick={() => onSelect(on ? null : r.userId)} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>
            <div className="row gap-8" style={{ alignItems: 'center', fontSize: 13 }}>
              <span style={{ width: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: on ? 700 : 500 }}>{r.name}</span>
              <span style={{ flex: 1, background: GRID, borderRadius: 5, height: 16, position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(v / max) * 100}%`, background: on ? 'var(--warning)' : 'var(--brand)', borderRadius: 5 }} />
              </span>
              <span className="num" style={{ width: 54, textAlign: 'right', fontWeight: 600 }}>{v}{unit}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Scatter({ reps, selRep }: { reps: VisitAnalytics['reps']; selRep: string | null }) {
  const { tip, bind } = useTip();
  const W = 760, H = 280, padL = 36, padB = 30, padT = 10, padR = 10;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  if (reps.length === 0) return <p className="faint small">No reps in scope.</p>;
  const maxX = Math.max(1, ...reps.map((r) => r.visits));
  const maxO = Math.max(1, ...reps.map((r) => r.orders));
  const xF = (v: number) => padL + (v / maxX) * innerW;
  const yF = (v: number) => padT + innerH - (v / 100) * innerH;
  return (
    <div className="table-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, fontVariantNumeric: 'tabular-nums' }} role="img" aria-label="Rep productivity scatter">
        {[0, 25, 50, 75, 100].map((t) => { const y = yF(t); return (
          <g key={t}><line x1={padL} y1={y} x2={W - padR} y2={y} stroke={GRID} /><text x={0} y={y + 3.5} fontSize="10" fill={AXIS}>{t}%</text></g>
        ); })}
        {[0, 0.5, 1].map((t) => { const x = padL + t * innerW; return (
          <text key={t} x={x} y={H - 10} fontSize="10" fill={AXIS} textAnchor="middle">{Math.round(maxX * t)} visits</text>
        ); })}
        {reps.map((r) => {
          const on = selRep === r.userId;
          const rad = 5 + 16 * (r.orders / maxO);
          return <circle key={r.userId} cx={xF(r.visits)} cy={yF(r.productivePct)} r={rad}
            fill={on ? 'var(--warning)' : 'rgba(241,94,34,0.4)'} stroke={on ? 'var(--warning)' : 'var(--brand)'} strokeWidth={1.4}
            {...bind(<div><strong>{r.name}</strong><div>{r.visits} visits · {r.productivePct}% productive · {r.orders} orders</div></div>)} />;
        })}
      </svg>
      <p className="small faint" style={{ marginTop: 4 }}>A rep far right but low is busy, not effective — the low-left dots are the ones to coach first.</p>
      <TipBox tip={tip} />
    </div>
  );
}

// =====================================================================
// Customize drawer
// =====================================================================
function CustomizeDrawer({ isAdmin, companyDefault, mine, onClose, onSaveCompany, onSaveMine }: {
  isAdmin: boolean; companyDefault: DashboardLayout | null; mine: DashboardLayout | null;
  onClose: () => void; onSaveCompany: (l: DashboardLayout) => Promise<void>; onSaveMine: (l: DashboardLayout | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState<'company' | 'user'>(isAdmin ? 'company' : 'user');
  const [draft, setDraft] = useState<Required<DashboardLayout>>(() => normalize(isAdmin ? companyDefault : (mine ?? companyDefault)));
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDraft(normalize(editing === 'company' ? companyDefault : (mine ?? companyDefault))); }, [editing, companyDefault, mine]);

  const toggleChart = (k: ChartKey) => setDraft((d) => ({ ...d, charts: { ...d.charts, [k]: !(d.charts[k] !== false) } }));
  const toggleKpi = (k: KpiKey) => setDraft((d) => ({ ...d, kpis: { ...d.kpis, [k]: !(d.kpis[k] !== false) } }));
  const move = (k: ChartKey, dir: -1 | 1) => setDraft((d) => {
    const o = [...d.order]; const i = o.indexOf(k); const j = i + dir;
    if (j < 0 || j >= o.length) return d;
    const tmp = o[i]!; o[i] = o[j]!; o[j] = tmp; return { ...d, order: o };
  });

  const save = async () => {
    setBusy(true);
    try { if (editing === 'company') await onSaveCompany(draft); else await onSaveMine(draft); onClose(); }
    catch { setBusy(false); }
  };
  const follow = async () => { setBusy(true); try { await onSaveMine(null); onClose(); } catch { setBusy(false); } };

  return (
    <div className="drawer-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="drawer" role="dialog" aria-label="Customize dashboard">
        <div className="drawer-head">
          <h3>Customize dashboard</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close" style={{ fontSize: 20, lineHeight: 1 }}>&times;</button>
        </div>
        {isAdmin && (
          <div className="tabs" style={{ margin: '12px 16px 0' }}>
            <button type="button" className={`tab${editing === 'company' ? ' active' : ''}`} onClick={() => setEditing('company')}>Company default</button>
            <button type="button" className={`tab${editing === 'user' ? ' active' : ''}`} onClick={() => setEditing('user')}>My view</button>
          </div>
        )}
        <p className="small faint" style={{ margin: '8px 16px 0' }}>
          {editing === 'company'
            ? 'Everyone in the company sees this, unless they set their own view.'
            : (mine ? 'You have a personal view — it overrides the company default for you.'
              : 'You are following the company default. Change anything to start your own view.')}
        </p>
        <div className="drawer-body">
          <div className="cfg-sec">Charts — show &amp; order</div>
          {draft.order.filter((k): k is ChartKey => (CHART_KEYS as readonly string[]).includes(k)).map((k, i, arr) => (
            <div key={k} className="cfg-row">
              <label className="switch">
                <input type="checkbox" checked={draft.charts[k] !== false} onChange={() => toggleChart(k)} />
                <span className="switch-track" />
              </label>
              <span style={{ flex: 1 }}>{CHART_TITLES[k]}</span>
              <span className="row gap-4">
                <button type="button" className="icon-btn sm" disabled={i === 0} onClick={() => move(k, -1)} aria-label="Move up">▲</button>
                <button type="button" className="icon-btn sm" disabled={i === arr.length - 1} onClick={() => move(k, 1)} aria-label="Move down">▼</button>
              </span>
            </div>
          ))}
          <div className="cfg-sec">KPI tiles</div>
          {KPI_KEYS.map((k) => (
            <div key={k} className="cfg-row">
              <label className="switch">
                <input type="checkbox" checked={draft.kpis[k] !== false} onChange={() => toggleKpi(k)} />
                <span className="switch-track" />
              </label>
              <span style={{ flex: 1 }}>{KPI_TITLES[k]}</span>
            </div>
          ))}
        </div>
        <div className="drawer-foot">
          {editing === 'user' && mine ? (
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void follow()}>Follow company default</button>
          ) : <span />}
          <div className="row gap-8">
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>Save</button>
          </div>
        </div>
      </aside>
    </div>
  );
}
